import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";

const steps = [
  {
    number: "1",
    title: "Estimation sur-mesure",
    content: "Nous réalisons une analyse précise de votre bien en le comparant aux biens vendus dans le même périmètre (base de données des notaires) et aux annonces de biens similaires en vente (outils de screening de l'ensemble des plateformes). Nous complétons cette estimation en nous appuyant sur les caractéristiques de votre bien (plan, distribution des pièces, possibilité d'aménagement, diagnostiques, DPE, travaux réalisés et/ou à prévoir …), les éléments liés à l'immeuble (ravalement effectué ou à prévoir, état financier de la copropriété, gardien, parties communes …) et sur l'environnement (commerces, sectorisation des écoles, espaces verts, transports …).\n\nNous vous présentons notre recommandation détaillée pour prendre les bonnes décisions ensemble."
  },
  {
    number: "2",
    title: "Stratégie de commercialisation",
    content: "Nous élaborons en fonction du prix, des caractéristiques de votre bien, du marché et de la date, un plan de mise sur le marché.\n\nNous prenons en charge l'ensemble des démarches nécessaires à la mise en vente de votre bien : la réalisation des diagnostics obligatoires, l'étude des derniers PV d'assemblée des copropriétaires, l'ensemble des informations relatives à l'immeuble, une évaluation des travaux à venir si besoin. Cela afin de fournir aux acquéreurs potentiels une information complète, fiable et transparente.\n\nNous mettons en valeur votre bien pour aider les acquéreurs potentiels à se projeter en faisant appel à des photographes professionnels et si besoin nous faisons du home-staging. Nous travaillons le texte de l'annonce en apportant toutes les informations pertinentes en fonction du profil des acheteurs potentiels et des supports de communication sélectionnés.\n\nPrésenter un bien dans les meilleures conditions ne relève pas du détail : cela fait une différence très concrète, à la fois sur le prix obtenu et sur le délai de vente.\n\nAvec votre accord et si cela est pertinent, nous pouvons prendre en charge le rangement, le désencombrement, de petites réparations, le nettoyage approfondi, jusqu'au lessivage des murs… Chaque intervention est pensée pour révéler le potentiel de votre bien et en optimiser la perception, donc la valeur. Nous organisons, si cela vous arrange, un service de ménage avant les visites et veillons à chaque détail de présentation, jusqu'à l'ajout de fleurs et de fruits, pour créer une atmosphère accueillante et soignée."
  },
  {
    number: "3",
    title: "Visites et reporting",
    content: "Nous sommes disponibles pour répondre aux appels et messages 7 jours sur 7 pour être certain de ne pas passer à côté d'un client. Nous qualifions les personnes intéressées avant de valider les visites. Nous nous organisons avec vous pour que les visites se déroulent aux moments qui vous conviennent pour que ça gêne le moins possible votre vie familiale et professionnelle. Chaque visite fait l'objet d'un retour détaillé qui peut permettre des ajustements de la stratégie si nécessaire."
  },
  {
    number: "4",
    title: "Négociation et sécurisation",
    content: "Nous analysons les offres, étudions et validons les profils acquéreurs et négocions les meilleures conditions pour vous. Notre double expertise chasseur et vendeur nous permet d'anticiper les objections et de défendre efficacement la valeur de votre bien.\n\nNous vous accompagnons dans toutes les étapes de la promesse de vente jusqu'à l'acte définitif."
  }
];

export const VentesPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  return (
    <div data-testid="ventes-page" className="pt-32">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-gold text-sm font-bold uppercase tracking-[0.2em] mb-4">
              Agent immobilier
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
              Nous vendons votre bien rapidement, au juste prix, auprès d'acheteurs qualifiés.
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Photo after intro - integrated */}
      <section className="py-0 bg-background">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-7xl mx-auto px-6 lg:px-8"
        >
          <div
            data-testid="ventes-image-placeholder"
            className="relative h-[450px] overflow-hidden"
          >
            <img
              src="https://res.cloudinary.com/dpeldfqpi/image/upload/v1784557410/iStock-2040192222_pihaan.jpg"
              alt="Vente immobilière Paris"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>
      </section>

      {/* Notre approche Section */}
      <section className="py-16 md:py-24 bg-surface">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-background border border-gold/30 p-8 md:p-12"
          >
            <h2 className="text-2xl font-bold text-text-primary mb-8">
              Notre approche de la vente
            </h2>
            <div className="space-y-6 text-text-secondary leading-relaxed">
              <p>
                Vendre un bien immobilier exige bien plus qu'une simple mise en relation : cela demande une connaissance fine du marché, une stratégie de prix rigoureuse et une parfaite maîtrise du processus de vente.
              </p>
              <p>
                Chez Emplacement-immo, nous appuyons chaque décision sur une analyse précise du marché local et des données concrètes, afin de positionner votre bien au prix juste, celui qui maximise sa valeur sans ralentir sa commercialisation. Nous ne bradons pas, nous ne survendons pas : nous construisons une stratégie sur-mesure.
              </p>
              <p>
                Nous mettons en valeur votre bien grâce à une stratégie marketing premium liée à notre double expertise de recherche et vente. En comprenant les acheteurs et leurs objectifs, nous savons mettre votre bien en valeur et le commercialiser en ciblant les clients potentiels. Nous travaillons uniquement avec des photographes professionnels et nous pouvons aussi faire du home-staging pour permettre aux clients de se projeter plus facilement. Nous faisons toujours le choix de l'authenticité. Nous ne recourons pas à des mises en scène numériques ou à des projections en 3D qui peuvent altérer la perception du bien. Nous préférons une présentation fidèle, exigeante et soignée, qui respecte votre bien et les acheteurs.
              </p>
              <p>
                Nous accordons une attention particulière à la qualité des acquéreurs. Grâce à une sélection exigeante, nous ne vous présentons que des profils sérieux, dont les capacités de financement sont validées, afin d'éviter les visites inutiles et les négociations incertaines.
              </p>
              <p>
                Enfin, nous prenons en charge l'ensemble du processus, des démarches obligatoires à la gestion des offres, pour vous offrir une vente fluide, sécurisée et parfaitement maîtrisée.
              </p>
              <p className="text-gold font-semibold">
                Notre objectif : obtenir le meilleur prix, dans les meilleurs délais, avec un niveau d'exigence constant à chaque étape.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Photo before steps */}
      <section className="py-0 bg-background">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-7xl mx-auto px-6 lg:px-8"
        >
          <div className="relative h-[400px] overflow-hidden">
            <img
              src="https://res.cloudinary.com/dpeldfqpi/image/upload/v1774894052/iStock-2153245482_kzzmab.jpg"
              alt="Expertise immobilière"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>
      </section>

      {/* Steps Section */}
      <section data-testid="ventes-steps-section" className="py-16 md:py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary text-center mb-16"
          >
            Les étapes de la vente
          </motion.h2>

          <div className="space-y-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`vente-step-${index}`}
                className="bg-background border border-line p-8"
              >
                <div className="flex items-baseline gap-6 mb-6">
                  <span className="font-serif text-gold text-5xl leading-none">{step.number}</span>
                  <h3 className="font-serif text-text-primary text-2xl">
                    {step.title}
                  </h3>
                </div>
                <p className="text-text-secondary leading-relaxed whitespace-pre-line">
                  {step.content}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-text-primary mb-6">
              Vous souhaitez vendre votre bien ?
            </h2>
            <p className="text-text-secondary mb-10 max-w-xl mx-auto">
              Contactez-nous pour une estimation gratuite et sans engagement de votre bien.
            </p>
            <Link to="/contact">
              <Button
                data-testid="cta-ventes-contact"
                className="bg-navy text-white hover:bg-navy hover:border-gold border border-navy font-semibold px-10 py-6 text-lg rounded-none"
              >
                Demander une estimation
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
